import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import { toast } from "react-toastify";

const PurchaseOrderSummary = () => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState([]);

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const [filters, setFilters] = useState({
        start_date: "",
        end_date: "",
        key: "",
        product: "",
        po: "",
    });

    const keyOptions = [
        { value: "dispatch_date", label: "Dispatch Date" },
        // { value: "order_date", label: "Order Date" },
        // { value: "delivery_date", label: "Delivery Date" },
        // { value: "rejected_date", label: "Rejected Date" },
        { value: "inspection_date", label: "Inspection Date" },
        // { value: "created_date", label: "Entry Date" },
    ];

    const getReport = async (page = 1, filterOverrides = {}) => {
        const token = getAuthToken();

        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        try {
            setLoading(true);

            const currentFilters = {
                ...filters,
                ...filterOverrides,
            };

            const payload = {
                start_date: currentFilters.start_date || null,
                end_date: currentFilters.end_date || null,
                key: currentFilters.key || null,
                product: currentFilters.product || null,
                po: currentFilters.po || null,
                page,
                per_page: 10,
            };

            const response = await axios.post(
                `${config.API}/senmanager/purchaseordersummary`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success) {
                const result = response.data?.data;

                setSummary(
                    Array.isArray(result)
                        ? result
                        : result?.data || []
                );

                setPagination({
                    current_page:
                        Number(result?.current_page) || page,
                    last_page:
                        Number(result?.last_page) || 1,
                    per_page:
                        Number(result?.per_page) || 10,
                    total:
                        Number(result?.total) || 0,
                });
            } else {
                setSummary([]);

                setPagination({
                    current_page: 1,
                    last_page: 1,
                    per_page: 10,
                    total: 0,
                });
            }
        } catch (error) {
            console.error(
                "Purchase Order Summary Error:",
                error
            );

            setSummary([]);

            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });

            toast.error(
                error.response?.data?.message ||
                    "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getReport(1);
    }, []);

    const clearFilters = () => {
        const clearedFilters = {
            start_date: "",
            end_date: "",
            key: "",
            product: "",
            po: "",
        };

        setFilters(clearedFilters);

        getReport(1, clearedFilters);
    };

    const totalQuantity = summary.reduce(
        (total, item) =>
            total + Number(item?.quantity || 0),
        0
    );

    const totalQuantityData = summary.reduce(
        (total, item) =>
            total + Number(item?.total_quantity_data || 0),
        0
    );

    const totalRejected = summary.reduce(
        (total, item) =>
            total + Number(item?.total_rejected || 0),
        0
    );

    const totalDispatched = summary.reduce(
        (total, item) =>
            total + Number(item?.total_dispatched || 0),
        0
    );

    const totalStock = summary.reduce(
        (total, item) =>
            total +
            Number(
                item?.stock_in_warehouse ??
                    item?.stock ??
                    0
            ),
        0
    );

    const startRecord =
        pagination.total > 0
            ? (pagination.current_page - 1) *
                  pagination.per_page +
              1
            : 0;

    const endRecord = Math.min(
        pagination.current_page *
            pagination.per_page,
        pagination.total
    );

    return (
        <div className="container-fluid mt-4">

            {/* PAGE HEADER */}
            <div className="mb-4">
                <h4 className="fw-semibold mb-1">
                    Purchase Order Summary
                </h4>

                <p className="text-muted mb-0">
                    View purchase order summary by date,
                    product and PO number.
                </p>
            </div>

            {/* FILTERS */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h6 className="fw-semibold mb-1">
                                Report Filters
                            </h6>

                            <small className="text-muted">
                                Select date range and search
                                criteria.
                            </small>
                        </div>

                        <button
                            type="button"
                            className="btn btn-light btn-sm border"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>
                    </div>

                    <div className="row g-3 align-items-end">

                        {/* START DATE */}
                        <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">
                                Start Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={filters.start_date}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        start_date:
                                            e.target.value,
                                    }))
                                }
                            />
                        </div>

                        {/* END DATE */}
                        <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">
                                End Date
                            </label>

                            <input
                                type="date"
                                className="form-control"
                                value={filters.end_date}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        end_date:
                                            e.target.value,
                                    }))
                                }
                            />
                        </div>

                        {/* KEY */}
                        <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">
                                Date Filter
                            </label>

                            <select
                                className="form-select"
                                value={filters.key}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        key: e.target.value,
                                    }))
                                }
                            >
                                <option value="">
                                    All Records
                                </option>

                                {keyOptions.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* PRODUCT */}
                        <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">
                                Product
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                placeholder="Product ID"
                                value={filters.product}
                                onChange={(e) => {
                                    const value =
                                        e.target.value;

                                    setFilters((prev) => ({
                                        ...prev,
                                        product: value,
                                    }));

                                    getReport(1, {
                                        product: value,
                                    });
                                }}
                            />
                        </div>

                        {/* PO NUMBER */}
                        <div className="col-12 col-md-2">
                            <label className="form-label fw-semibold">
                                PO Number
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                placeholder="PO Number"
                                value={filters.po}
                                onChange={(e) => {
                                    const value =
                                        e.target.value;

                                    setFilters((prev) => ({
                                        ...prev,
                                        po: value,
                                    }));

                                    getReport(1, {
                                        po: value,
                                    });
                                }}
                            />
                        </div>

                        {/* SEARCH BUTTON */}
                        <div className="col-12 col-md-2">
                            <button
                                type="button"
                                className="btn btn-primary w-100"
                                onClick={() =>
                                    getReport(1)
                                }
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />

                                        Loading...
                                    </>
                                ) : (
                                    "Search"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            <div className="card border-0 shadow-sm">

                <div className="card-header bg-white border-0 p-4">
                    <div className="d-flex justify-content-between align-items-center">

                        <div>
                            <h5 className="fw-semibold mb-1">
                                Purchase Order Summary
                            </h5>

                            <small className="text-muted">
                                {filters.start_date &&
                                filters.end_date
                                    ? `${filters.start_date} → ${filters.end_date}`
                                    : "Select a date range and click Search"}
                            </small>
                        </div>

                        <span className="badge bg-light text-dark border">
                            {pagination.total} Records
                        </span>
                    </div>
                </div>

                <div className="card-body p-0">

                    {loading ? (
                        <div className="text-center py-5">

                            <div
                                className="spinner-border text-primary"
                                role="status"
                            />

                            <div className="text-muted mt-3">
                                Loading purchase order
                                summary...
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">

                                <table className="table table-hover align-middle mb-0">

                                    <thead className="table-light">
                                        <tr>
                                            <th
                                                className="px-4"
                                                style={{
                                                    width: "70px",
                                                }}
                                            >
                                                #
                                            </th>

                                            <th className="px-4">
                                                PO NO
                                            </th>

                                            <th>
                                                PRODUCT
                                            </th>

                                            <th>
                                                CONSIGNEE
                                            </th>

                                            <th>
                                                INSPECTION DATE
                                            </th>

                                            <th>
                                                DELIVERY DATE
                                            </th>

                                            <th className="text-end">
                                                REQUIRED QUANTITY
                                            </th>

                                            <th className="text-end">
                                                DISPATCHED QUANTITY
                                            </th>

                                            <th className="text-end">
                                                STOCK
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {summary.length > 0 ? (
                                            summary.map(
                                                (item, index) => (
                                                    <tr
                                                        key={
                                                            item?.purchase_order_item_id ||
                                                            `${item?.product_name}-${index}`
                                                        }
                                                    >
                                                        <td className="px-4 text-muted">
                                                            {(pagination.current_page -
                                                                1) *
                                                                pagination.per_page +
                                                                index +
                                                                1}
                                                        </td>

                                                        <td className="px-4 fw-semibold">
                                                            {item?.po_number ||
                                                                "-"}
                                                            <small className="text-muted d-block">
                                                                {item?.erp_item_id ||
                                                                    "-"}
                                                            </small>
                                                        </td>

                                                        <td>
                                                            <div className="fw-semibold">
                                                                {item?.product_name ||
                                                                    "-"}
                                                            </div>
                                                        </td>

                                                        <td>
                                                            {item?.consignee ||
                                                                "-"}
                                                        </td>

                                                        <td>
                                                            {item?.inspection_date ||
                                                                "-"}
                                                        </td>

                                                        <td>
                                                            {item?.delivery_date ||
                                                                "-"}
                                                        </td>

                                                        <td className="text-end">
                                                            <span className="fw-semibold">
                                                                {Number(
                                                                    item?.quantity ||
                                                                        0
                                                                ).toLocaleString()}
                                                            </span>
                                                        </td>

                                                        <td className="text-end">
                                                            <span className="fw-semibold">
                                                                {Number(
                                                                    item?.dispatch_qty ||
                                                                        0
                                                                ).toLocaleString()}
                                                            </span>
                                                        </td>

                                                        <td className="text-end">
                                                            <span className="fw-semibold">
                                                                {Number(
                                                                    item?.stock_in_warehouse ??
                                                                        item?.stock ??
                                                                        0
                                                                ).toLocaleString()}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan="9"
                                                    className="text-center text-muted py-5"
                                                >
                                                    <div className="mb-2">
                                                        No purchase
                                                        order data
                                                        found.
                                                    </div>

                                                    <small>
                                                        Select filters
                                                        and search to
                                                        load data.
                                                    </small>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                    {summary.length > 0 && (
                                        <tfoot className="table-light">
                                            <tr>
                                                <th
                                                    colSpan="6"
                                                    className="px-4 text-end"
                                                >
                                                    PAGE TOTAL
                                                </th>

                                                <th className="text-end">
                                                    {totalQuantity.toLocaleString()}
                                                </th>

                                                <th className="text-end">
                                                    {totalDispatched.toLocaleString()}
                                                </th>

                                                <th className="text-end">
                                                    {totalStock.toLocaleString()}
                                                </th>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>

                            {/* PAGINATION */}
                            {pagination.last_page > 1 && (
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 border-top">

                                    <small className="text-muted">
                                        Showing{" "}
                                        <strong>
                                            {startRecord}
                                        </strong>{" "}
                                        to{" "}
                                        <strong>
                                            {endRecord}
                                        </strong>{" "}
                                        of{" "}
                                        <strong>
                                            {pagination.total}
                                        </strong>{" "}
                                        records
                                    </small>

                                    <nav>
                                        <ul className="pagination pagination-sm mb-0 flex-wrap">

                                            {/* PREVIOUS */}
                                            <li
                                                className={`page-item ${
                                                    pagination.current_page ===
                                                    1
                                                        ? "disabled"
                                                        : ""
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    onClick={() =>
                                                        getReport(
                                                            pagination.current_page -
                                                                1
                                                        )
                                                    }
                                                    disabled={
                                                        pagination.current_page ===
                                                        1
                                                    }
                                                >
                                                    Previous
                                                </button>
                                            </li>

                                            {/* PAGE NUMBERS */}
                                            {Array.from(
                                                {
                                                    length: pagination.last_page,
                                                },
                                                (_, i) =>
                                                    i + 1
                                            )
                                                .filter(
                                                    (page) =>
                                                        page >=
                                                            pagination.current_page -
                                                                2 &&
                                                        page <=
                                                            pagination.current_page +
                                                                2
                                                )
                                                .map((page) => (
                                                    <li
                                                        key={page}
                                                        className={`page-item ${
                                                            pagination.current_page ===
                                                            page
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="page-link"
                                                            onClick={() =>
                                                                getReport(
                                                                    page
                                                                )
                                                            }
                                                        >
                                                            {page}
                                                        </button>
                                                    </li>
                                                ))}

                                            {/* NEXT */}
                                            <li
                                                className={`page-item ${
                                                    pagination.current_page ===
                                                    pagination.last_page
                                                        ? "disabled"
                                                        : ""
                                                }`}
                                            >
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    onClick={() =>
                                                        getReport(
                                                            pagination.current_page +
                                                                1
                                                        )
                                                    }
                                                    disabled={
                                                        pagination.current_page ===
                                                        pagination.last_page
                                                    }
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderSummary;